module ContractsPageView

open FSharpPlus
open Client
open Fable.React
open Fable.React.Props
open ContractsPageModel
open PortalLibs.Shared
open PortalLibs.Client.Components
open PortalLibs.Client.Util
open Fulma
open Model
open Pages
open PortalLibs.Shared.Util
open PortalLibs.Client.Components.Dropdown
open Fulma.Extensions.Wikiki
open Feliz
open PortalLibs.Client.Components.Multiline
open PortalLibs.Client.Components.NewTableNext
open PortalLibs.Client.Model

let searchBarLevel left right =
  Level.level [] [
    Level.left [] [ Level.item [] left ]
    Level.right [] [
      Level.item [ Level.Item.CustomClass "report-search-col" ] right
    ]
  ]

let OnlyOneDropDownSpecific =
  OnlyOneDropdownGeneric<bool, unit, obj, obj>

let searchBar (model: ContractsPageModel.Model) dispatch =
  let searchViewChangedCallback =
    React.useCallback (SearchboxValueChanged >> dispatch, [||])

  let activeChangedCallback =
    React.useCallback (ActiveChanged >> dispatch, [||])

  div [ Style [ Margin "0 auto 10px auto" ] ] [
    searchBarLevel [ div [ Style [ Width "90px" ] ] [
                       OnlyOneDropDownSpecific
                         {| Title = Name "Active"
                            Items = ItemList [ true; false ]
                            SelectMode =
                              SingleSelect(
                                { UpdateModel = Simple activeChangedCallback
                                  SelectedItem = Item model.Active
                                  LinkUrl = None }
                              )
                            DisplayValue =
                              (fun m ->
                                match m with
                                | true -> "Yes"
                                | false -> "No")
                            Searchable = false
                            Type = Standard
                            Key = "ContractsPageContractActive" |}
                     ] ] [
      SearchboxNew.SearchboxNew
        {| Value = model.SearchboxValue
           IsLoading = false
           IconVisible = false
           ValueChanged = searchViewChangedCallback
           Key = "ContractsPageSearchbox"
           ShouldFocus = false |}
    ]
  ]

let booleanFollowedField (item: ContractShort) dispatch =
  NewTableCheckboxCell
    {| Key = $"FollowCheckbox-%s{item.Id.ToString()}"
       EditMode =
         ControlEditMode.Edit
           { OnChange =
               (fun value ->
                 ContractFollowChanged
                   { item with
                       Followed = value |> Option.defaultValue false }
                 |> dispatch)
             StopPropagation = false }
       Label = None
       Value = item.Followed |}


let NewTableNextSpecific =
  NewTableNextGeneric<ContractShort, ContractsPageViewColumn, unit>

let table (model: ContractsPageModel.Model) contracts dispatch =
  let tableMsgCallback =
    React.useCallback ((TableMsg >> dispatch), [||])

  let contractsFiltered =
    contracts
    |> List.filter
         (fun contract ->
           filterItemBySearchTerm
             [ (fun (i: ContractShort) -> i.Company)
               (fun (i: ContractShort) -> i.Name)
               (fun (i: ContractShort) -> i.ProjectManager) ]
             (model.SearchboxValue
              |> Option.noneIfEmptyStrOptTrim
              |> Option.defaultValue "")
             contract)

  let columns =
    [ { Header = Text "Company"
        Id = Company
        Align = Left
        Value = ValueType.Text(fun (item: ContractShort) -> item.Company)
        Sortable = ColumnSort.InMemory(fun item -> item.Company)
        Draggable = false
        MinWidth = Some 150
        Footer = None
        InitiallyVisible = true }
      { Header = Text "Contract"
        Id = ContractsPageViewColumn.Contract
        Align = Left
        Value =
          ValueType.CustomAligned
            (fun (item: ContractShort) ->
              a [ OnClick goToUrl
                  Href(contractUrl item.Id) ] [
                str item.Name
              ])
        Sortable = ColumnSort.InMemory(fun item -> item.Name)
        Draggable = false
        MinWidth = Some 250
        Footer = None
        InitiallyVisible = true }
      { Header = Text "Project Manager"
        Id = ContractsPageViewColumn.ProjectManager
        Align = Left
        Value = ValueType.Text(fun (item: ContractShort) -> item.ProjectManager)
        Sortable = ColumnSort.InMemory(fun item -> item.ProjectManager)
        Draggable = false
        MinWidth = Some 150
        Footer = None
        InitiallyVisible = true }
      { Header = Text "Current Contract Amount"
        Id = ContractsPageViewColumn.CurrentAmount
        Align = Right
        Value =
          ValueType.Text
            (fun (item: ContractShort) ->
              item.CurrentContractAmount
              |> DisplayNumber.AsCurrency.std)
        Sortable = ColumnSort.InMemory(fun item -> $"%30f{item.CurrentContractAmount}")
        Draggable = false
        MinWidth = Some 200
        Footer = None
        InitiallyVisible = true }
      { Header = Text "Interested Parties"
        Id = ContractsPageViewColumn.InterestedParties
        Align = Left
        Value =
          ValueType.CustomAligned
            (fun (item: ContractShort) ->
              Html.div [
                prop.style [
                  style.width (length.percent 100)
                ]
                prop.children [
                  Tooltip.divWithTooltip
                    "Enter one user login per line"
                    (Some Tooltip.TooltipPosition.Left)
                    (Multiline
                      {| Mode =
                           ReadWrite
                             { ValueChanged =
                                 (fun value ->
                                   ContractInterestedPartiesChanged({ item with InterestedParties = value })
                                   |> dispatch)
                               Spellcheck = false }
                         Value = item.InterestedParties
                         Key = $"ContractsPageInterestedParties-%i{item.Id}"
                         Rows = None
                         MaxRows = None
                         Ref = None |})
                ]
              ])
        Sortable = ColumnSort.InMemory(fun item -> item.InterestedParties)
        Draggable = false
        MinWidth = Some 250
        Footer = None
        InitiallyVisible = true }
      { Header =
          CheckboxLabel(
            "Follow",
            (fun (item: ContractShort) -> item.Followed),
            (fun (changed, checked') ->
              (changed |>> (fun item -> item.Id), checked')
              |> ContractFollowMassChanged
              |> dispatch)
          )
        Id = ContractsPageViewColumn.Follow
        Align = Center
        Value = ValueType.CustomAligned(fun (item: ContractShort) -> booleanFollowedField item dispatch)
        Sortable = ColumnSort.InMemory(fun item -> Boolean.toYesNoString item.Followed)
        Draggable = false
        MinWidth = Some 1
        Footer = None
        InitiallyVisible = true } ]

  div [ ClassName "contracts-page-table"
        Style [ Margin "auto" ] ] [

    yield
      NewTableNextSpecific
        {| Config =
             { Model = model.TableState
               Columns = columns
               Items = contractsFiltered
               ItemKey = fun item -> item.Id.ToString()
               SubRows = None
               ColumnSelection = false
               Selection = [], false
               Paging =
                 Paged
                   { HidePaginationUnder1Page = true
                     PageSizeOptions = None }
               Key = "ContractsPageTable"
               RowClick = None
               Dispatch = tableMsgCallback
               DataLoad = InMemory
               StickyHeader = None } |}
  ]

let content model dispatch contracts =
  div [ ClassName "contracts-page" ] [
    searchBar model dispatch
    table model contracts dispatch
  ]


let ContractsPageView =
  React.memo (
    "ContractsPageView",
    fun (props: {| model: Model
                   dispatch: Msg -> unit |}) ->
      div [ ClassName "contracts-page"
            Style [
              MarginTop "25px"
              MaxWidth "1600px"
              MarginLeft "auto"
              MarginRight "auto"
              PaddingBottom "10px"
              CSSProp.OverflowY OverflowOptions.Hidden
            ] ] [
        loadingMessageBoxOrContent
          "Loading Contracts..."
          (content props.model props.dispatch)
          props.model.Contracts
      ]
  )
